import type { ProjectReviewStatus } from '@dyeatlas/shared'
import { useQueries, useQuery } from '@tanstack/react-query'
import { ChevronDown, ExternalLink, FolderPlus, MoreHorizontal, Plus, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Field'
import { Card, EmptyState, LoadingPanel } from '@/components/ui/misc'
import { useDebounced } from '@/hooks/useDebounced'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'
import { InviteDialog, MembersDialog, NewWorkspaceDialog } from './dialogs'
import { AvatarStack, InviteButton, MemberRows } from './MemberList'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import type { WorkspaceDetail, WorkspaceRecord, WorkspaceSummary } from './types'

/** How many recent records the expanded overview card previews. */
const RECENT_PREVIEW = 3

export function WorkspaceOverview() {
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounced(query, 200)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [invitingId, setInvitingId] = useState<string | null>(null)
  const [membersOfId, setMembersOfId] = useState<string | null>(null)

  const workspaces = useQuery({
    queryKey: ['workspaces'],
    queryFn: () => api.get<{ workspaces: WorkspaceSummary[] }>('/workspaces'),
  })

  const all = workspaces.data?.workspaces ?? []

  const visible = useMemo(() => {
    const needle = debouncedQuery.trim().toLowerCase()
    if (!needle) return all
    return all.filter(
      (workspace) =>
        workspace.name.toLowerCase().includes(needle) ||
        workspace.description?.toLowerCase().includes(needle),
    )
  }, [all, debouncedQuery])

  // The member avatars appear on every card, so every workspace's detail is
  // fetched alongside the list rather than only when a card is expanded.
  const details = useQueries({
    queries: all.map((workspace) => ({
      queryKey: ['workspace', workspace.id],
      queryFn: () => api.get<WorkspaceDetail>(`/workspaces/${workspace.id}`),
    })),
  })

  const detailFor = (id: string) =>
    details.find((result) => result.data?.workspace.id === id)?.data

  const inviting = all.find((workspace) => workspace.id === invitingId)
  const membersOf = all.find((workspace) => workspace.id === membersOfId)

  return (
    <div>
      <div className="flex items-center justify-between gap-4 pb-5">
        <div className="flex items-baseline gap-3">
          <h1 className="font-display text-[28px] font-semibold leading-tight text-ink">
            Projects
          </h1>
          <span className="text-[13px] text-ink-muted">
            {all.length} project{all.length === 1 ? '' : 's'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative w-64">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-faint" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search projects..."
              className="h-9 pl-9"
            />
          </div>

          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="size-4" />
            New project
          </Button>
        </div>
      </div>

      {workspaces.isLoading ? (
        <LoadingPanel label="Loading your projects" />
      ) : all.length === 0 ? (
        <EmptyState
          icon={<FolderPlus className="size-6" />}
          title="No projects yet"
          description="A project is a private group where you and your collaborators can pool records — including drafts that are not in the Public Database."
          action={
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" />
              New project
            </Button>
          }
        />
      ) : visible.length === 0 ? (
        <EmptyState
          title="No project matches that search"
          description={`Nothing found for "${debouncedQuery}".`}
        />
      ) : (
        <div className="space-y-3">
          {visible.map((workspace) => (
            <WorkspaceCard
              key={workspace.id}
              workspace={workspace}
              detail={detailFor(workspace.id)}
              expanded={expandedId === workspace.id}
              onToggle={() =>
                setExpandedId((current) => (current === workspace.id ? null : workspace.id))
              }
              onInvite={() => setInvitingId(workspace.id)}
              onShowMembers={() => setMembersOfId(workspace.id)}
            />
          ))}
        </div>
      )}

      <NewWorkspaceDialog open={creating} onOpenChange={setCreating} />

      {inviting && (
        <InviteDialog
          workspace={inviting}
          open
          onOpenChange={(open) => !open && setInvitingId(null)}
        />
      )}

      {membersOf && (
        <MembersDialog
          workspace={membersOf}
          members={detailFor(membersOf.id)?.members ?? []}
          open
          onOpenChange={(open) => !open && setMembersOfId(null)}
          // Layered rather than swapped, so sending an invitation returns you to
          // the member list you were reading instead of to a bare page.
          onInvite={() => setInvitingId(membersOf.id)}
        />
      )}
    </div>
  )
}

function WorkspaceCard({
  workspace,
  detail,
  expanded,
  onToggle,
  onInvite,
  onShowMembers,
}: {
  workspace: WorkspaceSummary
  detail: WorkspaceDetail | undefined
  expanded: boolean
  onToggle: () => void
  onInvite: () => void
  onShowMembers: () => void
}) {
  const { user } = useCurrentUser()
  const memberCount = detail?.workspace.memberCount ?? workspace.memberCount

  const recent = useQuery({
    queryKey: ['workspace', workspace.id, 'records', 'recent'],
    queryFn: () =>
      api.get<{ records: WorkspaceRecord[] }>(
        `/workspaces/${workspace.id}/records?pageSize=${RECENT_PREVIEW}`,
      ),
    enabled: expanded,
  })

  return (
    <Card className={cn('overflow-hidden', expanded && 'border-brand-line')}>
      <div className="flex items-center gap-3 px-4 py-3.5">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <ChevronDown
            className={cn(
              'size-4 shrink-0 text-ink-faint transition-transform',
              !expanded && '-rotate-90',
            )}
          />
          <span className="min-w-0">
            <span className="flex items-center gap-2">
              <span className="truncate font-display text-[17px] font-semibold text-ink">
                {workspace.name}
              </span>
              <RoleBadge isOwner={workspace.isOwner} />
              <ProjectReviewPill status={workspace.reviewStatus} />
            </span>
            {workspace.description && (
              <span className="mt-0.5 block truncate text-[12px] text-ink-muted">
                {workspace.description}
              </span>
            )}
          </span>
        </button>

        <span className="shrink-0 text-[12px] text-ink-muted">
          {workspace.recordCount} record{workspace.recordCount === 1 ? '' : 's'}
        </span>

        <button type="button" onClick={onShowMembers} className="shrink-0">
          <AvatarStack
            members={detail?.memberPreview ?? []}
            total={detail?.workspace.memberCount ?? workspace.memberCount}
          />
        </button>

        <Link
          to={`/projects/${workspace.id}`}
          className="shrink-0 rounded-md p-1 text-ink-faint hover:bg-paper-muted hover:text-ink"
          aria-label={`Open ${workspace.name}`}
        >
          <MoreHorizontal className="size-4" />
        </Link>
      </div>

      {expanded && (
        <div className="grid gap-6 border-t border-line px-4 py-4 md:grid-cols-[1fr_300px]">
          <section>
            <div className="flex items-start justify-between gap-3 pb-2">
              <div>
                <h3 className="font-display text-[15px] font-semibold text-ink">
                  Recent records
                </h3>
                <p className="text-[12px] text-ink-muted">Records shared with this project</p>
              </div>
              <Link
                to={`/projects/${workspace.id}`}
                className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-[var(--radius-control)] border border-line bg-paper px-3 text-[13px] font-semibold text-ink hover:bg-paper-muted"
              >
                <ExternalLink className="size-3.5" />
                Open project
              </Link>
            </div>

            {recent.isLoading ? (
              <LoadingPanel label="Loading records" />
            ) : recent.data?.records.length === 0 ? (
              <p className="py-6 text-center text-[13px] text-ink-muted">
                Nothing shared here yet. Open one of your records and choose “Save to project”.
              </p>
            ) : (
              <ul className="space-y-2">
                {recent.data?.records.map((record) => (
                  <li key={record.id}>
                    <Link
                      to={`/projects/${workspace.id}?record=${record.id}`}
                      className="flex items-center gap-3 rounded-[var(--radius-control)] border border-line px-2.5 py-2 hover:bg-paper-muted"
                    >
                      <span
                        className="size-10 shrink-0 rounded-[6px] border border-line"
                        style={{ backgroundColor: record.swatchHex ?? '#ddd0c2' }}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-semibold text-ink">
                          {record.title}
                        </span>
                        <span className="block truncate text-[11px] text-ink-muted">
                          {record.code} · Added by {record.addedByName}
                        </span>
                      </span>
                      <StatusPill status={record.status} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <div className="flex items-start justify-between gap-2 pb-1">
              <div>
                <h3 className="font-display text-[15px] font-semibold text-ink">Members</h3>
                <p className="text-[12px] text-ink-muted">
                  {workspace.isOwner ? 'You are the owner' : 'You are a member'} ·{' '}
                  {memberCount} connected {memberCount === 1 ? 'person' : 'people'}
                </p>
              </div>
              {workspace.isOwner && <InviteButton onClick={onInvite} />}
            </div>

            <MemberRows
              members={detail?.memberPreview ?? []}
              ownerId={workspace.ownerId}
              canManage={workspace.isOwner}
              currentUserId={user?.id}
              onRemove={onShowMembers}
              removingId={null}
            />

            {detail && detail.members.length > detail.memberPreview.length && (
              <button
                type="button"
                onClick={onShowMembers}
                className="pt-2 text-[12px] text-ink-faint hover:text-ink-soft"
              >
                +{detail.members.length - detail.memberPreview.length} more members
              </button>
            )}
          </section>
        </div>
      )}
    </Card>
  )
}

export function RoleBadge({ isOwner }: { isOwner: boolean }) {
  return (
    <span
      className={cn(
        'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
        isOwner ? 'bg-brand-soft text-brand' : 'bg-paper-muted text-ink-muted',
      )}
    >
      {isOwner ? 'Owner' : 'Member'}
    </span>
  )
}

/** Where the project stands with the Public Database; nothing is shown before a first submission. */
export function ProjectReviewPill({ status }: { status: ProjectReviewStatus }) {
  if (status === 'none') return null
  const tones: Record<Exclude<ProjectReviewStatus, 'none'>, string> = {
    in_review: 'bg-caution-soft text-caution',
    published: 'bg-positive-soft text-positive',
    rejected: 'bg-brand-soft text-brand',
  }
  const labels: Record<Exclude<ProjectReviewStatus, 'none'>, string> = {
    in_review: 'Awaiting approval',
    published: 'Published',
    rejected: 'Changes requested',
  }
  return (
    <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold', tones[status])}>
      {labels[status]}
    </span>
  )
}

/** Mirrors the record's position in the peer-review workflow. */
export function StatusPill({ status }: { status: string }) {
  const tones: Record<string, string> = {
    published: 'bg-positive-soft text-positive',
    in_review: 'bg-caution-soft text-caution',
    rejected: 'bg-brand-soft text-brand',
    draft: 'bg-paper-muted text-ink-muted',
    finished: 'bg-info-soft text-info',
  }

  const labels: Record<string, string> = {
    published: 'Approved',
    in_review: 'Reviewing',
    rejected: 'Returned',
    draft: 'Draft',
    finished: 'Finished',
  }

  return (
    <span
      className={cn(
        'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold',
        tones[status] ?? 'bg-paper-muted text-ink-muted',
      )}
    >
      {labels[status] ?? status}
    </span>
  )
}
